import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  MoreVertical,
  Trash2,
  ClipboardList,
  HeartPulse,
  Utensils,
  TrendingUp,
  Bandage,
  Brain,
  Settings,
  Calendar,
  Clock,
  FileQuestion,
  Eye,
} from "lucide-react";
import {
  useClientCheckIns,
  CheckInAssignment,
  FREQUENCY_DISPLAY,
} from "@/hooks/use-client-check-ins";
import { CHECK_IN_CATEGORIES, CheckInCategory } from "@/hooks/use-check-in-templates";
import { AssignCheckInDialog } from "./AssignCheckInDialog";
import { CheckInResponsesDialog } from "./CheckInResponsesDialog";
import { toast } from "sonner";
import { format } from "date-fns";

const CATEGORY_ICONS: Record<CheckInCategory, React.ReactNode> = {
  general: <ClipboardList className="h-4 w-4" />,
  wellness: <HeartPulse className="h-4 w-4" />,
  nutrition: <Utensils className="h-4 w-4" />,
  progress: <TrendingUp className="h-4 w-4" />,
  injury: <Bandage className="h-4 w-4" />,
  mindset: <Brain className="h-4 w-4" />,
  custom: <Settings className="h-4 w-4" />,
};

const DAY_OF_WEEK_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface CheckInAssignmentRowProps {
  assignment: CheckInAssignment;
  onUnassign: (assignment: CheckInAssignment) => void;
  onViewResponses: (assignment: CheckInAssignment) => void;
}

function CheckInAssignmentRow({ assignment, onUnassign, onViewResponses }: CheckInAssignmentRowProps) {
  const template = assignment.template;
  if (!template) return null;

  const category = (template.category as CheckInCategory) || "general";
  const categoryMeta = CHECK_IN_CATEGORIES[category];
  const frequencyMeta = FREQUENCY_DISPLAY[assignment.frequency];

  // Format schedule info
  let scheduleInfo = frequencyMeta?.label || assignment.frequency;
  if (assignment.frequency === "weekly" && assignment.day_of_week !== null) {
    scheduleInfo = `Every ${DAY_OF_WEEK_LABELS[assignment.day_of_week]}`;
  } else if (assignment.frequency === "monthly" && assignment.day_of_month !== null) {
    scheduleInfo = `Monthly (day ${assignment.day_of_month})`;
  }

  // Format next due date
  const nextDueInfo = assignment.next_due_at
    ? `Next due: ${format(new Date(assignment.next_due_at), "MMM d")}`
    : null;

  return (
    <div className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-3">
        <div
          className={`h-9 w-9 rounded-lg bg-muted flex items-center justify-center ${categoryMeta.color}`}
        >
          {CATEGORY_ICONS[category]}
        </div>
        <div>
          <div className="font-medium text-sm">{template.name}</div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {scheduleInfo}
            </span>
            <span>•</span>
            <span>Started {format(new Date(assignment.start_date), "MMM d, yyyy")}</span>
            {nextDueInfo && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1 text-primary">
                  <Clock className="h-3 w-3" />
                  {nextDueInfo}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => onViewResponses(assignment)}>
          <Eye className="h-4 w-4 mr-1" />
          Responses
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => onUnassign(assignment)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

interface ClientCheckInsCardProps {
  clientId: string;
}

export function ClientCheckInsCard({ clientId }: ClientCheckInsCardProps) {
  const { assignments, responses, loading, unassignCheckIn, refetch, refetchResponses } =
    useClientCheckIns(clientId);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [responsesDialogOpen, setResponsesDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<CheckInAssignment | null>(null);
  const [unassignTarget, setUnassignTarget] = useState<CheckInAssignment | null>(null);
  const [isUnassigning, setIsUnassigning] = useState(false);

  const handleUnassign = async () => {
    if (!unassignTarget) return;

    setIsUnassigning(true);
    try {
      await unassignCheckIn(unassignTarget.id);
      toast.success("Check-in removed from client");
    } catch (error) {
      toast.error("Failed to remove check-in");
    } finally {
      setIsUnassigning(false);
      setUnassignTarget(null);
    }
  };

  const handleViewResponses = (assignment: CheckInAssignment) => {
    setSelectedAssignment(assignment);
    setResponsesDialogOpen(true);
  };

  const handleAssignSuccess = () => {
    setAssignDialogOpen(false);
    refetch();
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Assigned check-ins
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse text-muted-foreground">Loading check-ins...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Get unread/flagged response count
  const unreviewedCount = responses.filter((r) => !r.reviewed_at).length;
  const flaggedCount = responses.filter((r) => r.flagged).length;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Assigned check-ins
              {(unreviewedCount > 0 || flaggedCount > 0) && (
                <div className="flex gap-1">
                  {unreviewedCount > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {unreviewedCount} new
                    </Badge>
                  )}
                  {flaggedCount > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      {flaggedCount} flagged
                    </Badge>
                  )}
                </div>
              )}
            </CardTitle>
            <CardDescription>
              Questionnaires assigned to this client
            </CardDescription>
          </div>
          <Button onClick={() => setAssignDialogOpen(true)} size="sm" className="gap-2">
            <Plus className="h-4 w-4" />
            Assign check-in
          </Button>
        </CardHeader>
        <CardContent>
          {assignments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold mb-2">No check-ins assigned</h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                Assign check-in questionnaires to gather regular feedback from this client
                about their progress, wellness, and goals.
              </p>
              <Button onClick={() => setAssignDialogOpen(true)} variant="outline" className="gap-2">
                <Plus className="h-4 w-4" />
                Assign first check-in
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {assignments.map((assignment) => (
                <CheckInAssignmentRow
                  key={assignment.id}
                  assignment={assignment}
                  onUnassign={setUnassignTarget}
                  onViewResponses={handleViewResponses}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Check-in Dialog */}
      <AssignCheckInDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        clientId={clientId}
        existingTemplateIds={assignments.map((a) => a.template_id)}
        onSuccess={handleAssignSuccess}
      />

      {/* View Responses Dialog */}
      <CheckInResponsesDialog
        open={responsesDialogOpen}
        onOpenChange={setResponsesDialogOpen}
        clientId={clientId}
        assignment={selectedAssignment}
      />

      {/* Unassign Confirmation */}
      <AlertDialog open={!!unassignTarget} onOpenChange={() => setUnassignTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove check-in</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{unassignTarget?.template?.name}" from this client?
              Their response history will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnassigning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnassign}
              disabled={isUnassigning}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isUnassigning ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
